import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { System } from '../../shared-components/System';
import { Observable } from 'rxjs';
import { AdminAttendanceStudentPeriod, AttendanceMassEditFilter } from './attendance-mass-edit/attendance-mass-edit/attendance-mass-edit.model';
import { StudentAttendanceCodes } from './attendance-codes/attendance-codes.model';
import { YearService } from '../../shared-components/year/year.service';
import { AttendanceCheckInOut, CheckInOutSettings } from './attendance-check-in-out/attendance-check-in-out.model';
import { Attachment } from '../../shared-components/models/attachment.model';
import { AllStudentsAttendanceHistory, AttendanceByStudent } from './attendance-by-student/attendance-by-student.model';
import { CheckInOutSubmissionRequest } from '../../student360/student-summary/check-in-out-submission-request-modal/check-in-out-submission-request-modal.model';
import { AbsentNotice } from './review-absent-notices/absent-notice.model';

@Injectable({
    providedIn: 'root'
  })
export class AttendanceAdministrationService {

  attendanceMassEditFilter: AttendanceMassEditFilter;

    constructor(
        private http: HttpClient,
        private yearService: YearService
    ) { }

    getAdminStudentAttendance(siteYearId: number, startDate: string, endDate: string, studentNumbers: string[], lastName: string, firstName: string, studentListId: number, studentTeamId: number): Observable<AdminAttendanceStudentPeriod[]> {
        const queryString = '?siteYearId=' + siteYearId
            + '&startDate=' + startDate
            + '&endDate=' + endDate
            + '&lastName=' + lastName
            + '&firstName=' + firstName
            + '&studentListId=' + studentListId
            + '&studentTeamId=' + studentTeamId;
        return this.http.post<AdminAttendanceStudentPeriod[]>(System.apiURL.Admin + 'GetAdminStudentAttendance' + queryString, studentNumbers);
    }

    getStudentAttendanceCodes(yearId: number, siteId: number): Observable<StudentAttendanceCodes[]> {
        const queryString = '?yearId=' + yearId +
          '&siteId=' + siteId;
        return this.http.get<StudentAttendanceCodes[]>(System.apiURL.Admin + 'GetStudentAttendanceCodes' + queryString);
    }

    getAttendanceCheckInOutByDate(attendanceDate: string): Observable<AttendanceCheckInOut[]> {
      const queryString = '?yearId=' + this.yearService.defaultYear.Id +
        '&siteId=' + this.yearService.defaultYear.SchoolID +
        '&date=' + attendanceDate;
      return this.http.get<AttendanceCheckInOut[]>(System.apiURL.Admin + 'GetAttendanceCheckInOutByDate' + queryString);
    }

    saveCheckInOutHistoryAttachment(historyId: number, attachment: Attachment[]): Observable<boolean> {
      const queryString = '?historyId=' + historyId;
      return this.http.post<boolean>(System.apiURL.Admin + 'SaveCheckInOutHistoryAttachment' + queryString, attachment);
    }

    deleteCheckInOutRecordById(recordId: number, type: string): Observable<boolean> {
      const queryString = '?recordId=' + recordId +
      '&type=' + type;
      return this.http.get<boolean>(System.apiURL.Admin + 'DeleteCheckInOutRecordById' + queryString);
    }

    getSchoolDaysBetweenDates(siteYearId: number, startDate: string, endDate: string): Observable<Date[]> {
      const queryString = '?siteYearId=' + siteYearId +
      '&startDate=' + startDate +
      '&endDate=' + endDate;
      return this.http.get<Date[]>(System.apiURL.Admin + 'GetSchoolDaysBetweenDates' + queryString);
    }

    saveAdminStudentAttendance(siteYearId: number, attendance: AdminAttendanceStudentPeriod[]): Observable<boolean> {
      const queryString = '?siteYearId=' + siteYearId;
      return this.http.post<boolean>(System.apiURL.Admin + 'SaveAdminStudentAttendance' + queryString, attendance);
    }

    getAttendanceHistoryByStudentIdDate(studentId: number, attendanceDate: string, time: string, type: string, attendanceCodeId: number, dailyComment: string, overwritePriorPeriods: boolean, updatePeriodComment: boolean, periodCommentAppOROver: string, updateDailyComment: boolean, dailyCommentAppOROver: string): Observable<AttendanceByStudent[]> {
      const queryString = '?yearId=' + this.yearService.defaultYear.Id +
        '&siteId=' + this.yearService.defaultYear.SchoolID +
        '&studentId=' + studentId +
        '&attendanceDate=' + attendanceDate +
        '&time=' + time +
        '&type=' + type +
        '&attendanceCodeId=' + attendanceCodeId +
        '&dailyComment=' + dailyComment +
        '&overwritePriorPeriods=' + overwritePriorPeriods +
        '&updatePeriodComment=' + updatePeriodComment +
        '&periodCommentAppOROver=' + periodCommentAppOROver +
        '&updateDailyComment=' + updateDailyComment +
        '&dailyCommentAppOROver=' + dailyCommentAppOROver;
      return this.http.get<AttendanceByStudent[]>(System.apiURL.Admin + 'GetAttendanceHistoryByStudentIdDate' + queryString);
    }

    saveStudentAttendanceHistory(studentAttendance: AllStudentsAttendanceHistory[]): Observable<string> {
      const queryString = '?yearId=' + this.yearService.defaultYear.Id +
        '&siteId=' + this.yearService.defaultYear.SchoolID;
      return this.http.post<string>(System.apiURL.Admin + 'SaveStudentAttendanceHistory' + queryString, studentAttendance);
    }

    saveAttendanceHistoryCheckInOutByStudentIdDate(studentId: number, attendanceDate: string, time: string, type: string, attendanceCodeId: number, dailyComment: string, settings: CheckInOutSettings, clearAtt = false): Observable<boolean> {
      const queryString = '?yearId=' + this.yearService.defaultYear.Id +
        '&siteId=' + this.yearService.defaultYear.SchoolID +
        '&studentId=' + studentId +
        '&attendanceDate=' + attendanceDate +
        '&time=' + time +
        '&type=' + type +
        '&attendanceCodeId=' + attendanceCodeId +
        '&dailyComment=' + dailyComment +
        '&clearAtt=' + clearAtt;
      return this.http.post<boolean>(System.apiURL.Admin + 'SaveAttendanceHistoryCheckInOutByStudentIdDate' + queryString, settings);
    }

    getAttendanceSelfCheckInOutByStudentId(studentId: number): Observable<CheckInOutSubmissionRequest[]> {
      const queryString = '?yearId=' + this.yearService.defaultYear.Id +
        '&studentId=' + studentId;
      return this.http.get<CheckInOutSubmissionRequest[]>(System.apiURL.Admin + 'GetAttendanceCheckInOutSubmissionByStudentId' + queryString);
    }

    getCheckInOutHistoryAttachmentById(historyId: number, type: string): Observable<Attachment[]> {
      const queryString = '?historyId=' + historyId +
      '&type=' + type;
      return this.http.get<Attachment[]>(System.apiURL.Admin + 'GetCheckInOutHistoryAttachmentById' + queryString);
    }

    getAbsentNoticesByStudentIdAndYearId(studentId: number): Observable<AbsentNotice[]> {
      const queryString = '?yearId=' + this.yearService.defaultYear.Id +
      '&studentId=' + studentId;
      return this.http.get<AbsentNotice[]>(System.apiURL.Admin + 'GetAbsentNoticesByStudentIdAndYearId' + queryString);
    }

    getAbsentNoticeAttachmentById(id: number): Observable<Attachment[]> {
      const queryString = '?id=' + id;
      return this.http.get<Attachment[]>(System.apiURL.Admin + 'GetAbsentNoticeAttachmentById' + queryString);
    }

    processSubmissionRecord(historyId: number, submissionId: number): Observable<boolean> {
      const queryString = '?historyId=' + historyId +
      '&submissionId=' + submissionId;
      return this.http.get<boolean>(System.apiURL.Admin + 'ProcessSubmissionRecord' + queryString);
    }

    getAttendanceCheckInOutHistoryByStudentId(schoolId: number, studentId: number): Observable<AttendanceCheckInOut[]> {
      const queryString = '?yearId=' + this.yearService.defaultYear.Id +
        '&siteId=' + schoolId +
        '&studentId=' + studentId;
      return this.http.get<AttendanceCheckInOut[]>(System.apiURL.Admin + 'GetAttendanceCheckInOutHistoryByStudentId' + queryString);
    }
}
